import { useQueryClient } from '@tanstack/vue-query';
import { type NavigationGuard } from 'vue-router';
import { http, tryHttp } from '@/axios/instance/http';
import { fetchCyclePermissionsInfo } from '@/ccpv2/query/ccp-details';
import { fetchCycleInfo } from '@/ccpv2/query/ccp-settings';
import type { ConfigApiUserRole } from '@/ccpv2/types';
import type { Permission, PermissionsInfo } from '@/ccpv2/types/ccp-details';
import type { SettingsRole } from '@/ccpv2/types/ccp-settings';
import { useAuthStore } from '@/stores/auth';
import { useCompanyStore } from '@/stores/company';

export const companyUsesCcpV2: NavigationGuard = (to, from, next) => {
  const companyStore = useCompanyStore();
  const hasCcp = companyStore.hasCompCyclePlanning;

  if (hasCcp) {
    return next();
  } else {
    return next({ name: 'home' });
  }
};

export const redirectToHomeIfNotAdmin: NavigationGuard = async (to, from, next) => {
  const authStore = useAuthStore();

  if (authStore.isUserAdmin) {
    return next();
  } else {
    return next({ name: 'ccpv2-list' });
  }
};

export const redirectToHomeIfNoCcpAccess: NavigationGuard = async (to, from, next) => {
  const authStore = useAuthStore();

  if (authStore.appAccess.nav.comp_cycle) {
    return next();
  } else {
    return next({ name: 'home' });
  }
};

export const redirectToNotFoundIfNoWaveId: NavigationGuard = async (to, from, next) => {
  if (!to.params.waveId) {
    return next({ name: 'ccpv2-only-404' });
  }

  return next();
};

export const redirectToHomeIfNotPermission =
  (permission: Permission): NavigationGuard =>
  async (to, from, next) => {
    const { data, error } = await tryHttp(http.get<SettingsRole>(`v2/ccp/roles/${to.params.roleId}/permissions`));

    if (error || !data[permission]) {
      return next({ name: 'ccpv2-list' });
    }

    return next();
  };

export const loadCycleAndRedirectIfArchived: NavigationGuard = async (to, _from, next) => {
  const queryClient = useQueryClient();
  const cycleId = Number(to.params.cycleId);
  const isAdminRoute = to.name === 'ccpv2-admin-details';
  const role: ConfigApiUserRole = isAdminRoute ? 'admin' : 'planner';
  const permissionsVars = {
    roleId: isAdminRoute ? ('admin' as const) : Number(to.params.roleId),
  };

  const [cycle] = await Promise.all([
    queryClient.fetchQuery({
      queryKey: ['cycleInfo', { cycleId, role }],
      queryFn: async () => {
        const res = await fetchCycleInfo({ cycleId, role });
        return res.data;
      },
    }),
    queryClient.fetchQuery({
      queryKey: ['cyclePermissionsInfo', permissionsVars],
      queryFn: () => fetchCyclePermissionsInfo(permissionsVars),
    }),
  ]);

  if (cycle.hidden && !isAdminRoute) {
    return next({ name: 'ccpv2-list' });
  }

  return next();
};

export const redirectIfInactiveGroup: NavigationGuard = async (to, _from, next) => {
  const authStore = useAuthStore();

  if (authStore.isUserAdmin) {
    return next();
  }

  const queryClient = useQueryClient();

  const vars = { roleId: Number(to.params.roleId) };
  const permissionsInfo = queryClient.getQueryData<PermissionsInfo>(['cyclePermissionsInfo', vars]);

  if (!permissionsInfo?.is_active && !permissionsInfo?.allow_inactive_group_view) {
    return next({ name: 'ccpv2-list' });
  }

  return next();
};
